import {
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged as firebaseOnAuthStateChanged,
  sendPasswordResetEmail as firebaseSendPasswordResetEmail,
  EmailAuthProvider,
  reauthenticateWithCredential,
  updatePassword,
} from 'firebase/auth';
import { getFirebaseAuth, initializeFirebase } from '../config/firebase';

/**
 * 認証サービス
 * Firebase Authentication を利用したログイン・ログアウトを提供する。
 * ユーザー情報（ロール・権限）は /me API 経由で取得するため、
 * このサービスは Firebase 認証のみを担当する。
 */
class AuthService {
  /**
   * Firebase認証を初期化
   */
  initialize(): void {
    initializeFirebase();
  }

  /**
   * メール/パスワードでログイン
   */
  async login(email: string, password: string): Promise<void> {
    try {
      const auth = getFirebaseAuth();
      await signInWithEmailAndPassword(auth, email, password);
    } catch (error: unknown) {
      const code =
        error instanceof Error && 'code' in error ? (error as { code: string }).code : '';
      let message = 'ログインに失敗しました';

      if (code === 'auth/invalid-email') {
        message = 'メールアドレスの形式が正しくありません';
      } else if (code === 'auth/user-disabled') {
        message = 'このアカウントは無効化されています';
      } else if (code === 'auth/user-not-found') {
        message = 'ユーザーが見つかりません';
      } else if (code === 'auth/wrong-password') {
        message = 'パスワードが正しくありません';
      } else if (code === 'auth/invalid-credential') {
        message = 'メールアドレスまたはパスワードが正しくありません';
      } else if (code === 'auth/network-request-failed') {
        message = 'ネットワークエラーが発生しました';
      }

      throw new Error(message, { cause: error });
    }
  }

  /**
   * パスワード再設定メールを送信
   *
   * Firebase Auth が再設定リンクを含むメールを送信する。
   * ユーザー列挙を防ぐため、メールアドレスが未登録（`auth/user-not-found`）でも
   * 呼び出し側には成功として返す。
   *
   * @param email - 送信先メールアドレス
   */
  async sendPasswordResetEmail(email: string): Promise<void> {
    try {
      const auth = getFirebaseAuth();
      await firebaseSendPasswordResetEmail(auth, email);
    } catch (error: unknown) {
      const code =
        error instanceof Error && 'code' in error ? (error as { code: string }).code : '';

      if (code === 'auth/user-not-found') return;

      let message = 'リセットメールの送信に失敗しました';
      if (code === 'auth/invalid-email') {
        message = 'メールアドレスの形式が正しくありません';
      } else if (code === 'auth/too-many-requests') {
        message = '試行回数が多すぎます。しばらく時間をおいて再度お試しください';
      } else if (code === 'auth/network-request-failed') {
        message = 'ネットワークエラーが発生しました';
      }

      throw new Error(message, { cause: error });
    }
  }

  /**
   * ログイン中ユーザーのパスワードを変更する。
   *
   * 現パスワードで再認証（`reauthenticateWithCredential`）してから
   * `updatePassword` で新パスワードを設定する。
   *
   * @param currentPassword - 現在のパスワード
   * @param newPassword - 新しいパスワード
   */
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    const auth = getFirebaseAuth();
    const user = auth.currentUser;
    if (!user || !user.email) {
      throw new Error('ログイン情報を取得できません。再度ログインしてください');
    }

    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
    } catch (error: unknown) {
      const code =
        error instanceof Error && 'code' in error ? (error as { code: string }).code : '';

      let message = 'パスワードの変更に失敗しました';
      if (code === 'auth/wrong-password' || code === 'auth/invalid-credential') {
        message = '現在のパスワードが正しくありません';
      } else if (code === 'auth/weak-password') {
        message = '新しいパスワードが脆弱です（6 文字以上にしてください）';
      } else if (code === 'auth/requires-recent-login') {
        message = 'セキュリティのため再度ログインしてから操作してください';
      } else if (code === 'auth/too-many-requests') {
        message = '試行回数が多すぎます。しばらく時間をおいて再度お試しください';
      } else if (code === 'auth/network-request-failed') {
        message = 'ネットワークエラーが発生しました';
      }

      throw new Error(message, { cause: error });
    }
  }

  /**
   * ログアウト
   */
  async logout(): Promise<void> {
    try {
      const auth = getFirebaseAuth();
      await signOut(auth);
    } catch {
      throw new Error('ログアウトに失敗しました');
    }
  }

  /**
   * 認証状態の変更を監視
   * @param callback - Firebase ユーザーが認証済みかどうかを通知するコールバック
   * @returns 監視解除関数
   */
  onAuthStateChanged(callback: (isAuthenticated: boolean) => void): () => void {
    const auth = getFirebaseAuth();
    return firebaseOnAuthStateChanged(auth, (firebaseUser) => {
      callback(!!firebaseUser);
    });
  }
}

/** 認証サービスのシングルトンインスタンス */
export const authService = new AuthService();
